/* eslint no-console: 0 */
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useSetModalTitle } from '../../../hooks/modal/useSetModalTitle.ts';
import { useGetDevice } from '../../../hooks/useGetDevice.ts';
import { useUpdateDeviceProfileMutation } from '../../../hooks/useUpdateDeviceProfileMutation.ts';
import {
  MAP_MARKER_ICON_UPDATED_EVENT,
  MAP_MARKER_ICONS,
  defaultMapMarkerIconForModel,
} from '../../../services/DeviceMarkerIcons';
import screenNavigator from '../../../services/ScreenNavigator';
import Button from '../../bits/Button.jsx';
import { MapMarkerIcon } from '../../bits/MapMarkerIcon.tsx';
import Loading from '../Loading.jsx';

const Alert: React.FC<any> = props => (
  <MuiAlert elevation={6} variant="filled" {...props} />
);

const Intro = styled.p`
  line-height: 1.5;
  margin: 4px 0 20px;
`;

const IconGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fill, minmax(105px, 1fr));
`;

const IconChoice = styled.button<{ $selected: boolean }>`
  align-items: center;
  background: ${({ $selected }) => $selected ? '#e8f2fc' : '#ffffff'};
  border: 2px solid ${({ $selected }) => $selected ? '#4a8bcc' : '#d8d8d8'};
  border-radius: 12px;
  color: #4a4a4a;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font: inherit;
  font-weight: 600;
  gap: 9px;
  min-height: 110px;
  padding: 14px 8px;

  &:focus {
    box-shadow: 0 0 0 3px rgba(74, 139, 204, 0.3);
    outline: none;
  }
`;

const IconBadge = styled.span`
  align-items: center;
  display: flex;
  height: 64px;
  justify-content: center;
  width: 64px;
`;

const IconPreview = styled(MapMarkerIcon)`
  filter: drop-shadow(0 3px 3px rgba(27, 32, 36, 0.25));
  height: 68px;
  width: 68px;
`;

const ChoiceLabel = styled.span`
  font-size: 0.9em;
  line-height: 1.2;
  text-align: center;
`;

interface EditMapMarkerIconParams {
  id: string;
}

/**
 * Lets a customer preview and persist the map-marker icon for a device profile.
 */
const EditMapMarkerIcon: React.FC = () => {
  const { id: deviceId } = useParams<EditMapMarkerIconParams>();
  const { data: device, isLoading } = useGetDevice({ deviceId });
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const history = useHistory();
  const mutation = useUpdateDeviceProfileMutation();
  const queryClient = useQueryClient();

  useSetModalTitle('Map icon');

  const defaultIcon = defaultMapMarkerIconForModel(device?.model);
  const initialIcon = device?.activeProfile?.mapMarkerIcon || defaultIcon;

  useEffect(() => {
    if (device?.activeProfile) {
      setSelectedIcon(initialIcon);
    }
  }, [device?.activeProfile?.id, initialIcon]);

  const hasChanged = selectedIcon !== initialIcon;

  const onSave = useCallback(async () => {
    if (!device?.activeProfile) {
      setErrorMessage('This device does not have an active profile.');
      return;
    }

    try {
      await mutation.mutateAsync({
        id: device.activeProfile.id,
        mapMarkerIcon: selectedIcon,
      });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['useGetDevice', deviceId] }),
        queryClient.invalidateQueries({ queryKey: ['useGetUserDevices'] }),
      ]);

      window.dispatchEvent(new CustomEvent(MAP_MARKER_ICON_UPDATED_EVENT, {
        detail: {
          deviceId,
          mapMarkerIcon: selectedIcon || defaultIcon,
        },
      }));
      screenNavigator.exitCurrentScreen(history);
    } catch (error) {
      console.error('Failed to update map icon', error);
      setErrorMessage('Failed to update map icon');
    }
  }, [defaultIcon, device, deviceId, history, mutation, queryClient, selectedIcon]);

  return (
    <>
      {isLoading && <Loading />}

      {!isLoading && device && (
        <>
          <Intro>Choose how this tracker appears on live and location-history maps.</Intro>

          <IconGrid role="group" aria-label="Map icons">
            {MAP_MARKER_ICONS.map(icon => {
              const selected = selectedIcon === icon.id;
              return (
                <IconChoice
                  aria-pressed={selected}
                  key={icon.id}
                  onClick={() => setSelectedIcon(icon.id)}
                  $selected={selected}
                  type="button"
                >
                  <IconBadge>
                    <IconPreview icon={icon.id} />
                  </IconBadge>
                  <ChoiceLabel>{icon.label}</ChoiceLabel>
                </IconChoice>
              );
            })}
          </IconGrid>

          <div style={{ marginTop: '30px' }}>
            <Button
              color="primary"
              disabled={!hasChanged || mutation.isLoading}
              loading={mutation.isLoading}
              onClick={onSave}
            >Update</Button>
          </div>
        </>
      )}

      <Snackbar
        autoHideDuration={6000}
        onClose={() => setErrorMessage(null)}
        open={errorMessage !== null}
      >
        <Alert onClose={() => setErrorMessage(null)} severity="error">
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export { EditMapMarkerIcon };
